import React from "react";
import { QrCode, MapPin, X, AlertTriangle, PhoneCall } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useQr } from "../lib/qrContext";
import { LanguageGlobe } from "./LanguageGlobe";

const SAFETY_ASSIST_LOGO =
    "https://www.safety-assist.de/wp-content/uploads/2025/03/Safety-Assist-Logo-full-1.png";

// Notfall-Header: einheitliche grüne Leiste.
//   Ohne Branding-Logo → SafetyAssist-Chip (groß) + "Scan & Call"-Zeile.
//   Mit  Branding-Logo → Partner-Logo (links) ersetzt SafetyAssist + Scan-Call.
// Rechts sitzt immer das minimalistische Globus-Icon für die Sprachwahl.
export const EmergencyBanner = ({
    locationLabel,
    onClearLocation,
    showResolverWarning = false,
}) => {
    const { t } = useI18n();
    const { brandingLogoUrl, custom } = useQr();
    const showBranding = Boolean(brandingLogoUrl);

    return (
        <header
            data-testid="emergency-banner"
            className="fixed top-0 left-0 right-0 z-[1100]"
        >
            {showResolverWarning && (
                <div
                    data-testid="resolver-warning-banner"
                    className="bg-amber-500 text-zinc-900"
                >
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-1.5 flex items-center gap-2 text-xs sm:text-[13px]">
                        <AlertTriangle
                            className="w-4 h-4 flex-shrink-0"
                            strokeWidth={2.5}
                        />
                        <span className="font-display font-semibold truncate">
                            {t("resolver_warning_text")}
                        </span>
                    </div>
                </div>
            )}
            <div
                data-testid="scan-call-banner"
                className="bg-[#0F8040] text-white"
            >
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        {showBranding ? (
                            <div
                                data-testid="header-branding-logo"
                                className="inline-flex bg-white rounded-lg px-2.5 py-1.5"
                            >
                                <img
                                    src={brandingLogoUrl}
                                    alt={custom?.title || "Branding"}
                                    className="h-12 sm:h-14 w-auto max-w-[220px] sm:max-w-[320px] object-contain"
                                    loading="eager"
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <a
                                    data-testid="green-header-safetyassist-logo"
                                    href="/"
                                    onClick={(e) => {
                                        // Restart the flow from scratch –
                                        // full reload so all state resets.
                                        e.preventDefault();
                                        window.location.href = "/";
                                    }}
                                    className="inline-flex bg-white rounded-lg px-3 py-2 self-start opacity-95 hover:opacity-100 transition"
                                >
                                    <img
                                        src={SAFETY_ASSIST_LOGO}
                                        alt="SafetyAssist"
                                        className="h-12 sm:h-12 w-auto max-w-[220px] sm:max-w-[280px] object-contain"
                                        loading="eager"
                                    />
                                </a>
                                <div className="flex items-center gap-2.5">
                                    <QrCode
                                        className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0"
                                        strokeWidth={2.5}
                                    />
                                    <div
                                        data-testid="header-scan-call-text"
                                        className="font-display font-bold text-lg sm:text-2xl tracking-tight"
                                    >
                                        {t("header_scan_call")}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex-shrink-0">
                        <LanguageGlobe variant="onGreen" />
                    </div>
                </div>
            </div>

            {/* Persistent 112 quick-dial bar. Always the EU number, never
                the per-object custom number – this bar is a safety net so
                the visitor always has a one-click way to reach 112. */}
            <div
                data-testid="header-112-bar"
                className="bg-white border-b border-zinc-200"
            >
                <div className="max-w-6xl mx-auto px-3 sm:px-6 py-1.5 sm:py-2 flex items-center justify-end">
                    <a
                        data-testid="header-112-button"
                        href="tel:112"
                        className="inline-flex items-center gap-2 bg-[#c70000] hover:bg-[#a30000] active:scale-[0.98] transition-all text-white font-display font-black text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#c70000]/30"
                        aria-label={t("header_112_aria")}
                    >
                        <PhoneCall className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={3} />
                        112
                    </a>
                </div>
            </div>

            {locationLabel && (
                <div
                    data-testid="saved-location-banner"
                    className="bg-zinc-900 text-white"
                >
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-1.5 sm:py-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <MapPin
                                className="w-4 h-4 flex-shrink-0"
                                strokeWidth={2.5}
                            />
                            <div
                                data-testid="saved-location-label"
                                className="font-display font-semibold text-xs sm:text-sm tracking-tight truncate"
                            >
                                {locationLabel}
                            </div>
                        </div>
                        {onClearLocation && (
                            <button
                                data-testid="saved-location-clear"
                                onClick={onClearLocation}
                                aria-label={t("header_clear_location")}
                                className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center transition"
                            >
                                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};
